// `extension-alert-create-client-contact-api.ng1.ts` — "Create in ATS" on a contact's profile, and
// the employer autocomplete its form searches (`typeaheads.getCreateClientContactExistingEmployers`).
//
// ⚠️ `createInAts` WRITES INTO THE CUSTOMER'S ATS. It is enqueued server-side and answers a
// `messageId` the async-operations poll later resolves (`client-contact-async-operations.ts`);
// the same endpoint serves both "Create new contact" and "Update existing contact", told apart by
// `context` alone. It is tested against a stubbed `fetch` (`atsWrites.test.ts`) and must never be
// called against a tenant from a probe — intercept it.
//
// Every shape here was read off the upstream `*.api.ng1.ts` and `AddAtsClientContactController`,
// not inferred from the name (CLAUDE.md: the paths were the only part `packages/api` got right):
//
//   createInAts                                  POST  JSON body = the params
//   getValidationResponse                        POST  JSON body = the SAME params; read-only
//                                                      (`AddAtsClientContactService.getValidationResponse`
//                                                      queries the WL DB for a duplicate email and writes nothing)
//   getLastAddedAtsClientTracking                GET   ?clientContactId=        → `{ creationDate }` or an EMPTY body
//   getByWhitelabelAndNewClientContactExternalId GET   ?newClientContactExternalId=
//   employerAutoComplete                         GET   ?query=  (NOT `q`)        → `[{ employerExternalId, employerValue }]`
import { request } from './request.ts'
import type { ClientContactAsyncMessageResponse } from './client-contact-async-operations.ts'

/** `AddAtsClientContactContext` — which of the modal's two buttons sent the request. */
export const AddAtsClientContactContext = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
} as const
export type AddAtsClientContactContext = (typeof AddAtsClientContactContext)[keyof typeof AddAtsClientContactContext]

/**
 * `IAddAtsClientContactParams` — the body of both POSTs, in upstream's field order.
 *
 * ⚠️ `null` AND ABSENT ARE DIFFERENT VALUES ON THE WIRE, and the form produces both. `$http`
 * serialises with `angular.toJson`, which drops `undefined` and keeps `null`; `JSON.stringify` does
 * the same, so a field this port leaves `undefined` where upstream leaves it `undefined` (the
 * phone of a contact with an EMPTY `phoneNumbers` array, say) is absent on both sides. Normalising
 * either into the other would make the two bodies differ on a field neither user touched.
 */
export interface AddAtsClientContactParams {
  email: string | null | undefined
  name: string | null | undefined
  clientContactExternalId: string | undefined
  clientContactId: number
  alertId?: number | undefined
  phone?: string | null | undefined
  city?: string | null | undefined
  region?: string | null | undefined
  country?: string | null | undefined
  companyId: number | null
  employerExternalId: string | null
  jobTitle?: string | null | undefined
  newCompanyCity?: string | null | undefined
  newCompanyRegion?: string | null | undefined
  newCompanyCountry?: string | null | undefined
  context: AddAtsClientContactContext
}

/**
 * `IAddAtsClientContactValidationResponse`. `validationErrors` is a Java `Map<String, String>`,
 * which Jackson writes as a plain object — and `null` when the params are valid.
 */
export interface AddAtsClientContactValidationResponse {
  valid: boolean
  validationErrors?: Record<string, string> | null
}

/** `IAddAtsClientContactTrackingDto` — epoch millis of the last contact created from this one. */
export interface AddAtsClientContactTracking {
  creationDate: number
}

/** `IClientContactAddAtsCompletedOperationResult` — `result` is the NEW contact's external id. */
export interface ClientContactAddAtsCompletedOperationResult {
  successful: boolean
  result?: string | null
}

/** `IEmployerAutoCompleteResult` (`typeaheads.service.ng1.ts:26`). */
export interface EmployerAutoCompleteResult {
  employerExternalId: string
  employerValue: string
}

export async function createInAts(params: AddAtsClientContactParams): Promise<ClientContactAsyncMessageResponse | null> {
  return request('POST', '/chrome-extension-api/client-contact/createInAts', params)
}

export async function getCreateClientContactValidationResponse(
  params: AddAtsClientContactParams,
): Promise<AddAtsClientContactValidationResponse> {
  return request('POST', '/chrome-extension-api/client-contact/getValidationResponse', params)
}

/**
 * ⚠️ AN EMPTY 200 MEANS "NEVER CREATED", NOT A FAILURE. The controller answers
 * `ResponseEntity.ok(null)` when there is no tracking row, which is a 200 with no body; `request`
 * reads that as `null`.
 */
export async function getLastAddedAtsClientTracking(clientContactId: number): Promise<AddAtsClientContactTracking | null> {
  return request(
    'GET',
    `/chrome-extension-api/client-contact/getLastAddedAtsClientTracking?${new URLSearchParams({
      clientContactId: String(clientContactId),
    }).toString()}`,
  )
}

/** Declared upstream and called by nothing in the extension; ported for completeness of the API. */
export async function getByWhitelabelAndNewClientContactExternalId(
  newClientContactExternalId: string,
): Promise<AddAtsClientContactTracking | null> {
  return request(
    'GET',
    `/chrome-extension-api/client-contact/getByWhitelabelAndNewClientContactExternalId?${new URLSearchParams({
      newClientContactExternalId,
    }).toString()}`,
  )
}

/**
 * `getCreateClientContactExistingEmployers` — the "Select a <ATS> company" field.
 *
 * ⚠️ `query`, NOT `q`. Its neighbours on the same service (`searchCompanySizes`, the skills and
 * titles) all take `q`; this one is `@RequestParam String query`, and a `q` is a 400.
 */
export async function getCreateClientContactExistingEmployers(query: string): Promise<EmployerAutoCompleteResult[]> {
  const found = await request<EmployerAutoCompleteResult[] | null>(
    'GET',
    `/chrome-extension-api/client-contact/employerAutoComplete?${new URLSearchParams({ query }).toString()}`,
  )
  return found ?? []
}
